import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { CreditCard, CheckCircle2, Copy, Building, Smartphone, UploadCloud, MapPin, AlertCircle, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { useUIStore } from "@/store/ui-store";
import { useAuthStore } from "@/store/auth-store";
import type { Product } from "@/types/catalog";
import type { Order } from "@/types/order";
import type { PaymentGateway } from "@/types/payment";

export function CheckoutPage() {
  const nav = useNavigate();
  const { cart, clearCart } = useUIStore();
  const { user } = useAuthStore();
  
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  
  // 🛠️ Generate ONE idempotency key when the component mounts. 
  // This guarantees that even if the user clicks "Submit" 100 times due to network lag,
  // the backend will ONLY process the very first request and return the same order!
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const variantIds = Object.keys(cart).map(Number);

  // 1. Load Cart Products
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["cart-products", variantIds],
    queryFn: () => get<Product[]>("/catalog/products"),
    enabled: variantIds.length > 0,
  });

  // 2. Load Payment Gateways
  const { data: gateways, isLoading: gatewaysLoading } = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: () => get<PaymentGateway[]>("/public/payment-gateways"),
  });

  // 3. Compute Cart Totals
  const lines = useMemo(() => {
    if (!products) return [];
    const map = new Map<number, { product: Product; variantId: number; price: number; sku: string }>();
    for (const p of products) {
      for (const v of p.variants) {
        map.set(v.id, { product: p, variantId: v.id, price: Number(v.price), sku: v.sku });
      }
    }
    return variantIds
      .map((id) => {
        const info = map.get(id);
        if (!info) return null;
        const qty = cart[id];
        return { ...info, quantity: qty, lineTotal: info.price * qty };
      })
      .filter(Boolean) as any[];
  }, [products, variantIds, cart]);

  const total = lines.reduce((sum, l) => sum + l.lineTotal, 0);

  // 4. Form Setup (Strict Validation)
  const { register, handleSubmit, formState: { errors, isSubmitting }, trigger } = useForm({
    defaultValues: {
      address: user?.address || "",
      sender_account: "",
      transaction_reference: "",
    }
  });

  // Handle file selection for visual feedback
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFileName(e.target.files[0].name);
    } else {
      setSelectedFileName(null);
    }
  };

  // 5. The Ultimate Double-Mutation (Place Order -> Submit Payment)
  const submitCheckout = useMutation({
    mutationFn: async (data: any) => {
      if (!selectedGateway) throw new Error("Please select a payment method.");

      const formData = new FormData();
      
      // Order Items as a JSON string
      const itemsPayload = lines.map((l) => ({ variant_id: l.variantId, quantity: l.quantity }));
      formData.append("items", JSON.stringify(itemsPayload));
      formData.append("address", data.address);
      
      // Payment Details
      formData.append("method", selectedGateway.name);
      formData.append("sender_account", data.sender_account);
      formData.append("amount", String(total));
      formData.append("transaction_reference", data.transaction_reference);
      
      const fileInput = document.getElementById("payment-proof") as HTMLInputElement;
      if (fileInput?.files?.[0]) formData.append("screenshot", fileInput.files[0]);

      // 🛠️ Calling the unified atomic endpoint!
      return post<{ order_id: string; payment_id: string }>("/customer/unified-checkout", formData, {
        idempotencyKey: idempotencyKey,
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    onSuccess: (res) => {
      toast.success("Order placed and payment submitted successfully!");
      clearCart();
      nav(`/dashboard/orders/${res.order_id}`, { replace: true });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to complete checkout. Please check your transaction details.");
    }
  });

  // Safe manual submit handler
  const onSubmit = async (data: any) => {
    if (!selectedGateway) {
      toast.error("You must select a payment method before confirming.");
      return;
    }
    const isValid = await trigger();
    if (isValid) {
      submitCheckout.mutate(data);
    }
  };

  if (productsLoading || gatewaysLoading) return <div className="p-10"><Skeleton className="h-[500px] max-w-4xl mx-auto rounded-3xl" /></div>;
  
  if (lines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
        <ShoppingCart className="w-16 h-16 text-surface-300 mb-4" />
        <h2 className="text-2xl font-bold text-surface-900 mb-2">Your cart is empty</h2>
        <p className="text-surface-500 mb-6">Looks like you haven't added anything to your cart yet.</p>
        <Button onClick={() => nav("/dashboard/catalog")}>Browse Catalog</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10">
      <header className="text-center py-6 sm:py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-surface-900 tracking-tight">Checkout</h1>
        <p className="text-surface-500 mt-2 text-sm sm:text-base">Review your items and complete payment securely.</p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="grid lg:grid-cols-5 gap-8 items-start">
        
        {/* LEFT: Order Summary & Address (2 cols) */}
        <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-24">
          <Card className="rounded-2xl border-0 shadow-elevate-1 bg-surface-50 overflow-hidden ring-1 ring-surface-200/50">
            <CardHeader className="bg-surface-100/50 border-b border-surface-200">
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <ul className="divide-y divide-surface-200/60 px-5">
                {lines.map((item: any) => (
                  <li key={item.variantId} className="py-4 flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="font-bold text-surface-900 leading-tight">{item.product.name}</div>
                      <div className="text-xs text-surface-500 font-mono mt-1 bg-white inline-block px-1.5 py-0.5 rounded border border-surface-200">
                        {item.sku}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-surface-900">{formatCurrency(item.lineTotal)}</div>
                      <div className="text-xs text-surface-500 mt-0.5">Qty: {item.quantity}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="p-5 bg-white flex justify-between items-center border-t border-surface-200">
                <span className="text-sm font-bold uppercase tracking-wider text-surface-500">Total</span>
                <span className="text-2xl sm:text-3xl font-extrabold text-brand-700 tracking-tight">{formatCurrency(total)}</span>
              </div>
            </CardBody>
          </Card>

          {/* Shipping Address Form */}
          <Card className="rounded-2xl border-0 shadow-elevate-1">
            <CardHeader><CardTitle><MapPin className="w-5 h-5 text-brand-600"/> Shipping Details</CardTitle></CardHeader>
            <CardBody>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">Full Delivery Address *</label>
              <textarea
                {...register("address", { required: "Delivery address is required" })}
                className={clsx(
                  "w-full h-24 px-3.5 py-2.5 bg-surface-50 border rounded-xl text-sm resize-none focus:bg-white transition-colors",
                  errors.address ? "border-danger-500 focus:ring-2 focus:ring-danger-500/20" : "border-surface-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                )}
                placeholder="Enter your house number, street, city, and zip code..."
              />
              {errors.address && (
                <div className="mt-1.5 flex items-center gap-1 text-xs text-danger-600 font-medium">
                  <AlertCircle className="w-3.5 h-3.5" /> {errors.address.message as string}
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* RIGHT: Payment Gateways (3 cols) */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="rounded-2xl border-0 shadow-elevate-2">
            <CardHeader className="border-b border-surface-100 flex items-center justify-between">
              <CardTitle>Select Payment Method</CardTitle>
              {errors.transaction_reference && !selectedGateway && (
                <span className="text-xs text-danger-600 font-semibold animate-pulse">Required</span>
              )}
            </CardHeader>
            <CardBody className="p-5 sm:p-6">
              {(gateways ?? []).length === 0 ? (
                <div className="text-center text-sm font-semibold text-warning-700 p-6 bg-warning-50 rounded-xl border border-warning-200">
                  No payment methods available. Contact support.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {gateways!.map(gw => (
                    <button
                      type="button"
                      key={gw.id}
                      onClick={() => setSelectedGateway(gw)}
                      className={clsx(
                        "relative p-4 sm:p-5 rounded-2xl border-2 transition-all flex flex-col items-center justify-center text-center gap-3",
                        selectedGateway?.id === gw.id 
                          ? "border-brand-500 bg-brand-50/50 shadow-md ring-4 ring-brand-500/10 scale-[1.02]" 
                          : "border-surface-200 bg-white hover:border-brand-300 hover:shadow-sm"
                      )}
                    >
                      {selectedGateway?.id === gw.id && (
                        <div className="absolute top-3 right-3 text-brand-600 bg-white rounded-full">
                          <CheckCircle2 className="w-5 h-5 fill-brand-100"/>
                        </div>
                      )}
                      
                      {gw.logo ? (
                        <img src={gw.logo} alt={gw.name} className="h-10 sm:h-12 object-contain" />
                      ) : (
                        gw.provider_type === "mfs" ? <Smartphone className="h-10 w-10 text-brand-600"/> : <Building className="h-10 w-10 text-surface-600"/>
                      )}
                      <span className="font-bold text-surface-900 text-sm">{gw.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Payment Details Form */}
              {selectedGateway && (
                <div className="mt-8 pt-8 border-t border-surface-200 animate-slide-up space-y-6">
                  <div className="p-5 bg-surface-50 border border-surface-200 rounded-2xl">
                    <div className="text-xs font-bold uppercase tracking-wider text-surface-500 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Send Money To
                    </div>
                    <div className="flex items-center justify-between bg-white p-3 sm:p-4 rounded-xl border border-surface-200 shadow-sm">
                      <span className="font-mono text-xl sm:text-2xl font-bold tracking-wider text-surface-900">{selectedGateway.account_details}</span>
                      <button type="button" onClick={() => { navigator.clipboard.writeText(selectedGateway.account_details); toast.success("Copied to clipboard!"); }} className="p-2.5 hover:bg-brand-50 rounded-lg text-brand-600 transition-colors bg-surface-50">
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                    {selectedGateway.instructions && (
                      <div className="mt-4 p-4 bg-white rounded-xl text-sm text-surface-700 leading-relaxed border border-surface-200">
                        {selectedGateway.instructions}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <Input 
                      label="Sender Account Number *" 
                      placeholder="e.g. 017XXXXXX" 
                      disabled={submitCheckout.isPending}
                      {...register("sender_account", { required: "Please enter the number you sent money from" })} 
                      error={errors.sender_account?.message as string}
                    />

                    <Input 
                      label="Transaction ID / Reference Number *" 
                      placeholder="e.g. TRXZA12345678" 
                      disabled={submitCheckout.isPending}
                      {...register("transaction_reference", { required: "Transaction ID is required to verify your payment" })} 
                      error={errors.transaction_reference?.message as string}
                    />
                    
                    <div>
                      <label className="block text-sm font-medium text-surface-800 mb-1.5">Payment Screenshot (Optional)</label>
                      <label className={clsx(
                        "flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors group relative overflow-hidden",
                        submitCheckout.isPending ? "opacity-50 cursor-not-allowed bg-surface-50 border-surface-300" : "bg-surface-50 hover:bg-surface-100 border-surface-300"
                      )}>
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 text-surface-500 group-hover:text-brand-600 transition-colors">
                          <UploadCloud className="w-8 h-8 mb-3" />
                          <p className="text-sm font-semibold">{selectedFileName ? selectedFileName : "Click to upload screenshot"}</p>
                        </div>
                        <input id="payment-proof" type="file" accept="image/*" className="hidden" disabled={submitCheckout.isPending} onChange={handleFileChange} />
                      </label>
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      fullWidth 
                      leftIcon={<CheckCircle2 className="w-5 h-5"/>} 
                      loading={submitCheckout.isPending || isSubmitting} 
                      disabled={submitCheckout.isPending || isSubmitting}
                      className="h-14 text-lg rounded-xl shadow-brand-500/25 mt-4"
                    >
                      {submitCheckout.isPending ? "Processing..." : "Confirm Checkout & Place Order"}
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </form>
    </div>
  );
}